import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  AvatarSmall,
  IconLocation,
} from '../../components';
import { formatMinimalPriceForDisplay, formatPriceForDisplay } from './priceDisplay';
import { getCategoryIconUrl } from '../../containers/PageBuilder/SectionBuilder/SectionCategoryDirectory/categoryDirectoryPresentation';

import {
  getShortLocation,
  getTaskBudgetValue,
  getTaskCategory,
  getTaskDeadline,
  getTaskFormat,
} from './taskCardHelpers';
import css from './ListingCard.module.css';

// D11: task cards use 16:9 media, like a wide service card image.
const TASK_MEDIA_ASPECT_WIDTH = 16;
const TASK_MEDIA_ASPECT_HEIGHT = 9;

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const AUTHOR_FALLBACK_IMAGE_VARIANTS = [
  'square-small',
  'square-small2x',
  'square-xsmall',
  'square-xsmall2x',
];

/**
 * D14 search-card fallback: the author's profile photo as card media. The search response already
 * includes the card-ratio variants (`${variantPrefix}`, 400w and `${variantPrefix}-2x`, 800w) for
 * profile images too, so the photo stays sharp at 2× on frames up to ~400px; the square variants
 * remain as fallbacks. ResponsiveImage skips any variant that is not present.
 *
 * @param {string} variantPrefix listing image variant prefix (e.g. "listing-card")
 * @returns {Array<string>} variant names, largest-coverage first
 */
export const getAuthorFallbackImageVariants = variantPrefix => [
  variantPrefix,
  `${variantPrefix}-2x`,
  ...AUTHOR_FALLBACK_IMAGE_VARIANTS,
];
// Strict mapping of the self-declared provider_type user field (publicData) to badge message ids.
// Unknown values (including prototype-like keys) resolve to null, so no badge is rendered.
const getProviderTypeBadgeMessageId = providerType => {
  switch (providerType) {
    case 'provider-professional':
      return 'ListingCard.providerTypeBadge.professional';
    case 'provider-non-professional':
      return 'ListingCard.providerTypeBadge.nonProfessional';
    default:
      return null;
  }
};

/** Normalize listing text excerpts for service-card display (whitespace collapse). */
const normalizeServiceCardText = value =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';

/** Hosted label for fulfillmentModes option "online", or null if unavailable. */
const getHostedOnlineFulfillmentLabel = listingFields => {
  if (!Array.isArray(listingFields)) {
    return null;
  }
  const field = listingFields.find(f => f?.key === 'fulfillmentModes');
  const options = field?.enumOptions;
  if (!Array.isArray(options)) {
    return null;
  }
  const onlineOption = options.find(o => o?.option === 'online');
  const label = onlineOption?.label;
  return typeof label === 'string' && label.trim() ? label.trim() : null;
};

/**
 * Resolve optional service metadata from fulfillmentModes.
 * Online always wins; offline-only uses publicData.location.address.
 */
const resolveServiceMetadata = (publicData, onlineLabel) => {
  const modes = publicData?.fulfillmentModes;
  if (!Array.isArray(modes)) {
    return null;
  }
  const hasOnline = modes.includes('online');
  if (hasOnline) {
    return onlineLabel ? { kind: 'online', text: onlineLabel } : null;
  }
  if (modes.includes('offline')) {
    const address = normalizeServiceCardText(publicData?.location?.address);
    return address ? { kind: 'offline', text: address } : null;
  }
  return null;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

// First-line price display for direct-contact-service cards. The value is stored as a raw string
// in publicData.minimalPrice (e.g. "458" or "по договорённости"); see formatMinimalPriceForDisplay.
const MinimalPriceMaybe = props => {
  const { publicData } = props;
  const display = formatMinimalPriceForDisplay(publicData?.minimalPrice);
  if (!display) {
    return null;
  }

  return (
    <div className={classNames(css.price, css.minimalPrice)} title={display}>
      <span className={css.priceValue}>{display}</span>
    </div>
  );
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

// A2-06: the first row of a result list (index 0–3 at every width; SSR does not know the
// viewport) is requested eagerly, the first card with fetchpriority="high" (as the hero feed
// does); later cards and cards rendered without an index are lazy.
const FIRST_ROW_CARDS = 4;
const getImageLoadingProps = index =>
  Number.isInteger(index) && index < FIRST_ROW_CARDS
    ? { loading: 'eager', ...(index === 0 ? { fetchpriority: 'high' } : {}) }
    : { loading: 'lazy' };

// `listingId` keys the SVG clip ids: useId gave different ids on the server and the client
// (the trees above the card differ), which React reported on every search page load (backlog #4, H4).
const ProviderRatingMaybe = ({ author, listingId }) => {
  const uid = listingId;
  const intl = useIntl();
  const publicData = author?.attributes?.profile?.publicData;
  const averageRating = publicData?.averageRating;
  const reviewCount = publicData?.reviewCount;

  const hasRating = reviewCount > 0 && averageRating != null;

  // No reviews: render nothing (no "0.0 (0)" placeholder, no reserved line).
  if (!hasRating) {
    return null;
  }

  const rating = Number(averageRating);
  // §8.5: the value in the locale's format («4,8» in Russian); the clip ids keep the fixed form.
  const displayRating = intl.formatNumber(rating, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const ratingId = rating.toFixed(1).replace('.', '-');
  const displayCount = reviewCount;

  return (
    <div className={css.ratingRow}>
      <span className={css.ratingStars}>
        {Array.from({ length: 5 }).map((_, index) => {
          const fill = Math.min(Math.max(rating - index, 0), 1);
          const clipId = `star-${uid}-${ratingId}-${index}`;
          return (
            <svg
              key={clipId}
              className={css.ratingStarWrapper}
              viewBox="0 0 10 10"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <defs>
                <clipPath id={clipId}>
                  <rect x="0" y="0" width={fill * 10} height="10" />
                </clipPath>
              </defs>
              <path
                fill="var(--delowoLightSteel)"
                d="M5 1l1.12 2.27L9 3.64l-2 1.95.47 2.75L5 7.02 2.53 8.34 3 5.59 1 3.64l2.88-.37z"
              />
              <path
                fill="var(--delowoRatingStar)"
                d="M5 1l1.12 2.27L9 3.64l-2 1.95.47 2.75L5 7.02 2.53 8.34 3 5.59 1 3.64l2.88-.37z"
                clipPath={`url(#${clipId})`}
              />
            </svg>
          );
        })}
      </span>
      <span className={css.ratingValue}>{displayRating}</span>
      <span className={css.ratingCount}>({displayCount})</span>
    </div>
  );
};

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { priceTitle } = priceData(price, config.currency, intl);

  // §9 display format: «180 €», «45,50 €» (cents are no longer rounded away).
  const formattedPriceForCard = price && price.currency ? formatPriceForDisplay(intl, price) : '';

  const priceValue = <span className={css.priceValue}>{formattedPriceForCard}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * Neutral card media placeholder (D11, D14): the level-1 category icon and name on a neutral
 * surface; an empty neutral box without a category. Used by task cards without a photo and by
 * service cards whose author has no profile photo.
 *
 * @component
 * @param {Object} props
 * @param {{id: string, name: string}?} props.category level-1 category
 * @param {string?} props.className extra class (e.g. a compact variant for small tiles)
 * @returns {JSX.Element} placeholder filling its media frame
 */
export const ListingMediaPlaceholder = ({ category, className }) => {
  const iconUrl = category ? getCategoryIconUrl(category.id) : null;
  return (
    <div className={classNames(css.mediaPlaceholder, className)}>
      {iconUrl ? (
        <img
          className={css.mediaPlaceholderIcon}
          src={iconUrl}
          alt=""
          aria-hidden="true"
          width="40"
          height="40"
        />
      ) : null}
      {category?.name ? <span className={css.mediaPlaceholderName}>{category.name}</span> : null}
    </div>
  );
};

/**
 * ListingCardImage
 * The media part of the listing card in a fixed aspect ratio frame (D14): the listing's first
 * photo; else the author's profile photo cropped to the card ratio (search results and the /u
 * profile alike); else the neutral category placeholder; a listing without an author gets the
 * template thumbnail.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
    badge = null,
    category = null,
    imageLoadingProps = {},
  } = props;

  // 1) Listing image (cover) — только она через ResponsiveImage/LazyImage
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const listingImageVariants = firstImage
    ? Object.keys(firstImage?.attributes?.variants || {}).filter(k => k.startsWith(variantPrefix))
    : [];

  const author = currentListing?.author;

  // Рендер:
  // A) Если для типа листинга разрешены картинки и есть cover — показываем cover
  if (showListingImage && firstImage) {
    return (
      <AspectRatioWrapper
        className={css.listingImageWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={listingImageVariants}
          sizes={renderSizes}
          {...imageLoadingProps}
        />
        {badge}
      </AspectRatioWrapper>
    );
  }

  // B) Fallback to the author's profile photo cropped to the card ratio (D14), on every service card list
  // (search and the /u profile): the listing-card variants are in both responses, so it stays sharp at 2×.
  if (author?.profileImage) {
    return (
      <AspectRatioWrapper
        className={css.listingImageWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <ResponsiveImage
          rootClassName={classNames(css.rootForImage, css.searchResultAuthorFallbackImage)}
          alt={title}
          image={author.profileImage}
          variants={getAuthorFallbackImageVariants(variantPrefix)}
          sizes={renderSizes}
          {...imageLoadingProps}
        />
        {badge}
      </AspectRatioWrapper>
    );
  }

  // C) No profile photo either: the neutral category placeholder (D14), never initials
  if (author) {
    return (
      <AspectRatioWrapper
        className={css.listingImageWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <ListingMediaPlaceholder category={category} />
        {badge}
      </AspectRatioWrapper>
    );
  }

  // D) Иначе — заглушка
  return (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

// Calendar glyph of IconDate with a viewBox, so it scales to the 12px metadata size.
const IconDeadline = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    aria-hidden="true"
  >
    <path d="M5.33398.666504c.36819 0 .66667.298477.66667.666666v.66667h4.00005v-.66667c0-.368189.2984-.666666.6666-.666666.3682 0 .6667.298477.6667.666666v.66667h1.8667c.8627 0 1.4666.74388 1.4666 1.53846v9.5897c0 .7946-.6039 1.5385-1.4666 1.5385H2.80065c-.86279 0-1.46667-.7439-1.46667-1.5385V3.5383c0-.79458.60388-1.53846 1.46667-1.53846h1.86667v-.66667c0-.368189.29847-.666666.66666-.666666ZM4.66732 3.33317H2.80065c-.01729 0-.0441.0063-.07449.03942-.03127.03408-.05884.09093-.05884.16571v2.46154H13.334V3.5383c0-.07478-.0276-.13163-.0589-.16571-.0303-.03312-.0572-.03942-.0744-.03942H11.334v.66667c0 .36819-.2985.66666-.6667.66666-.3682 0-.6666-.29847-.6666-.66666v-.66667H6.00065v.66667c0 .36819-.29848.66666-.66667.66666-.36819 0-.66666-.29847-.66666-.66666v-.66667Zm8.66668 4H2.66732V13.128c0 .0748.02757.1317.05884.1657.03039.0332.0572.0395.07449.0395H13.2007c.0172 0 .0441-.0063.0744-.0395.0313-.034.0589-.0909.0589-.1657V7.33317Z" />
  </svg>
);

/**
 * Task card media (D11): the first listing photo in a 16:9 frame, or a neutral placeholder
 * with the level-1 category icon and name. The «Задача» badge sits on the media.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {{id: string, name: string}?} props.category level-1 category
 * @param {string} props.badgeLabel «Задача» badge text rendered over the media
 * @param {string?} props.className replaces the card's image-wrapper class (e.g. map info card)
 * @returns {JSX.Element} media frame
 */
export const TaskCardMedia = props => {
  const {
    currentListing,
    title,
    renderSizes,
    variantPrefix,
    setActivePropsMaybe,
    category,
    badgeLabel,
    className,
    imageLoadingProps = {},
  } = props;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants || {}).filter(k => k.startsWith(variantPrefix))
    : [];
  return (
    <AspectRatioWrapper
      className={classNames(className || css.listingImageWrapper, css.taskMedia)}
      width={TASK_MEDIA_ASPECT_WIDTH}
      height={TASK_MEDIA_ASPECT_HEIGHT}
      {...setActivePropsMaybe}
    >
      {firstImage ? (
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
          {...imageLoadingProps}
        />
      ) : (
        <ListingMediaPlaceholder category={category} />
      )}
      {badgeLabel ? <span className={css.taskBadge}>{badgeLabel}</span> : null}
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {number?} props.index position in a result list (first row loads eagerly, A2-06)
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    visualVariant = 'default',
    index,
  } = props;
  const isSearchResultCard = visualVariant === 'searchResult';
  const imageLoadingProps = getImageLoadingProps(index);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description, price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const authorProfilePublicData = author?.attributes?.profile?.publicData;
  const hasVisibleRating =
    authorProfilePublicData?.reviewCount > 0 && authorProfilePublicData?.averageRating != null;

  const { listingType, cardStyle } = publicData || {};
  const isTask = listingType === 'tasks';
  const classes = classNames(rootClassName || css.root, className, {
    [css.searchResultCard]: isSearchResultCard,
    [css.taskCard]: isTask,
  });

  const isDirectContactService = listingType === 'direct-contact-service';
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const priceContent = isDirectContactService ? (
    <MinimalPriceMaybe publicData={publicData} />
  ) : (
    <PriceMaybe
      price={price}
      publicData={publicData}
      config={config}
      intl={intl}
      listingTypeConfig={foundListingTypeConfig}
    />
  );

  const hostedOnlineLabel = !isTask
    ? getHostedOnlineFulfillmentLabel(config.listing?.listingFields)
    : null;
  const serviceMetadata = !isTask ? resolveServiceMetadata(publicData, hostedOnlineLabel) : null;
  const serviceDescriptionPreview = !isTask ? normalizeServiceCardText(description) : '';
  const showServiceProvider = !isTask && showAuthorInfo;
  const showServiceRating = !isTask && hasVisibleRating;
  const showServiceFooter = showServiceProvider || showServiceRating;
  // D11 task card data (display only)
  const taskBudgetValue = isTask ? getTaskBudgetValue(publicData, intl, config.currency) : null;
  const taskDeadline = isTask ? getTaskDeadline(publicData, intl) : null;
  const taskLocationText = isTask ? getShortLocation(publicData?.location?.address) : '';
  const taskDescriptionPreview = isTask ? normalizeServiceCardText(description) : '';
  const taskFormat = isTask ? getTaskFormat(publicData, config.listing?.listingFields) : null;
  // Level-1 category for the media placeholder (tasks, and services without any photo)
  const listingCategory = getTaskCategory(publicData, config.categoryConfiguration);

  // Provider type badge follows author visibility: searchResult service cards only.
  const providerTypeBadgeMessageId =
    showServiceProvider && isSearchResultCard
      ? getProviderTypeBadgeMessageId(authorProfilePublicData?.provider_type)
      : null;
  const providerTypeBadge = providerTypeBadgeMessageId ? (
    <span className={css.providerTypeBadge}>
      {intl.formatMessage({ id: providerTypeBadgeMessageId })}
    </span>
  ) : null;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      {isTask ? (
        <>
          <TaskCardMedia
            currentListing={currentListing}
            title={title}
            renderSizes={renderSizes}
            variantPrefix={variantPrefix}
            setActivePropsMaybe={setActivePropsMaybe}
            category={listingCategory}
            badgeLabel={intl.formatMessage({ id: 'ListingCard.taskType' })}
            imageLoadingProps={imageLoadingProps}
          />
          <div
            className={classNames(css.serviceInfo, css.taskInfo, {
              [css.searchResultInfo]: isSearchResultCard,
            })}
          >
            <div className={classNames(css.serviceTitle, css.title, css.taskTitle)}>
              {isSearchResultCard
                ? title
                : richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
            </div>
            <div
              className={classNames(css.servicePrice, {
                [css.searchResultPriceSlot]: isSearchResultCard,
              })}
            >
              {taskBudgetValue ? (
                <div className={classNames(css.price, css.taskBudget)} title={taskBudgetValue}>
                  <span className={css.taskBudgetLabel}>
                    {intl.formatMessage({ id: 'ListingCard.budgetLabel' })}
                  </span>
                  <span className={css.priceValue}>{taskBudgetValue}</span>
                </div>
              ) : null}
            </div>
            {taskLocationText ? (
              <div className={classNames(css.serviceMetadata, css.taskLocation)}>
                <IconLocation className={css.serviceMetadataIcon} />
                <span className={css.serviceMetadataText}>{taskLocationText}</span>
              </div>
            ) : null}
            {taskDescriptionPreview ? (
              <div className={classNames(css.serviceDescription, css.taskDescription)}>
                {taskDescriptionPreview}
              </div>
            ) : null}
            <div className={css.taskFooter}>
              <span className={css.taskFormat}>{taskFormat || ''}</span>
              <span
                className={classNames(css.taskDeadline, {
                  [css.taskNoDeadline]: !taskDeadline.hasDeadline,
                })}
              >
                <IconDeadline className={css.taskDeadlineIcon} />
                {taskDeadline.text}
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          <ListingCardImage
            renderSizes={renderSizes}
            title={title}
            currentListing={currentListing}
            config={config}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            style={cardStyle}
            showListingImage={showListingImage}
            badge={providerTypeBadge}
            category={listingCategory}
            imageLoadingProps={imageLoadingProps}
          />
          <div
            className={classNames(css.serviceInfo, {
              [css.searchResultInfo]: isSearchResultCard,
            })}
          >
            <div className={classNames(css.serviceTitle, css.title)}>
              {isSearchResultCard
                ? title
                : richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
            </div>
            <div
              className={classNames(css.servicePrice, {
                [css.searchResultPriceSlot]: isSearchResultCard,
              })}
            >
              {priceContent}
            </div>
            {serviceMetadata ? (
              <div
                className={classNames(css.serviceMetadata, {
                  [css.serviceMetadataOffline]: serviceMetadata.kind === 'offline',
                })}
              >
                {serviceMetadata.kind === 'offline' ? (
                  <IconLocation className={css.serviceMetadataIcon} />
                ) : null}
                <span className={css.serviceMetadataText}>{serviceMetadata.text}</span>
              </div>
            ) : null}
            {serviceDescriptionPreview ? (
              <div className={css.serviceDescription}>{serviceDescriptionPreview}</div>
            ) : null}
            {showServiceFooter ? (
              <div className={css.serviceFooter}>
                {showServiceProvider && isSearchResultCard ? (
                  <div className={css.serviceProvider}>
                    <AvatarSmall
                      user={author}
                      disableProfileLink={true}
                      className={css.serviceProviderAvatar}
                    />
                    <span className={css.serviceProviderName}>{authorName}</span>
                  </div>
                ) : null}
                {showServiceProvider && !isSearchResultCard ? (
                  <div className={css.serviceProvider}>
                    <span className={css.serviceProviderName}>
                      <FormattedMessage id="ListingCard.author" values={{ authorName }} />
                    </span>
                  </div>
                ) : null}
                {showServiceRating ? (
                  <div className={css.serviceRating}>
                    <ProviderRatingMaybe author={author} listingId={id} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      )}
    </NamedLink>
  );
};

export default ListingCard;
